import type { FC } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldTextArea } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import type { HygieneUpdateModalProps } from "@/components/modals/profil-hygiene/hygiene-update-modal";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { patchDietSchema, patchSportsSchema } from "@/schemas/patient";
import { resetForm } from "@/utils/form";

const HygieneModal: FC<HygieneUpdateModalProps> = ({
  patient,
  hygieneModalOpen,
  setHygieneModalOpen,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { dietComment, sportsComment } = patient;

  const isDiet = hygieneModalOpen === "diet";

  const schema = isDiet ? patchDietSchema : patchSportsSchema;

  const { Field, handleSubmit } = useForm({
    defaultValues: isDiet
      ? {
          dietComment: dietComment ? dietComment : null,
        }
      : { sportsComment: sportsComment ? sportsComment : null },
    onSubmit: async ({ value, formApi }) => {
      const values = schema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setHygieneModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      open={hygieneModalOpen !== "none"}
      onOpenChange={() => setHygieneModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldTextArea
          Field={Field}
          schema={schema}
          name={isDiet ? "dietComment" : "sportsComment"}
          placeholder="Écrire..."
        />
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <DialogFooter className="flex gap-3 px-6">
        <Button
          type="button"
          className="w-full"
          onClick={() => {
            setHygieneModalOpen("none");
          }}
          variant={"outline"}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          className="w-full"
          variant={"blue"}
          onClick={() => {
            handleSubmit();
          }}
        >
          Générer
        </Button>
      </DialogFooter>
    </DialogCustom>
  );
};

export default HygieneModal;
